import * as React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, ChevronRight,
  Image as ImageIcon, Mic, Radar, Shield, User,
} from 'lucide-react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { VoiceAgent, preloadConvaiScript } from '@xyqar/shared-voice';
import { uploadOnboardingAsset } from '../components/wizard/fileAssets';
import { AUTH_RETURN_TO_KEY } from '../authReturnTo';
import type { UploadedAsset } from '../components/wizard/fileAssets';

const API_BASE =
  (import.meta as any).env?.VITE_API_BASE ?? 'https://api.xyqar.com';
const GENESIS_URL =
  (import.meta as any).env?.VITE_GENESIS_URL ?? 'https://genesis.xyqar.com';

function buildGenesisReturnUrl() {
  const url = new URL(GENESIS_URL);
  url.searchParams.set('return_to', window.location.href);
  return url.toString();
}

type Lane = 'advisor' | 'ambassador';
type Step = 'consent' | 'lane' | 'background' | 'interview';

const ADVISOR_TRACKS = [
  'Strategic growth advisor', 'Enterprise sales advisor',
  'Public sector & defense advisor', 'Cyber trust advisor',
  'CISO circle advisor', 'International expansion advisor',
  'Fundraising advisor', 'Partnership advisor',
  'DeepTech narrative advisor', 'Board governance advisor',
];

const AMBASSADOR_TRACKS = [
  'Regional chapter ambassador', 'Executive dinner host',
  'Ecosystem connector', 'Thought leadership ambassador',
  'Talent scout ambassador', 'Campus / community relay',
];

const ARIA_PROFILES = [
  { value: 'cfo',   label: 'CFO / Finance',     desc: 'Financial strategy, fundraising, runway' },
  { value: 'legal', label: 'Legal / Compliance', desc: 'Regulatory, contracts, IP, data' },
  { value: 'phd',   label: 'PhD / Research',     desc: 'Technical depth, methodology, evidence' },
  { value: 'sales', label: 'Sales / GTM',         desc: 'Pipeline, deals, partnerships, enterprise' },
];

interface S {
  step: Step;
  consentData: boolean;
  consentRecord: boolean;
  consentCCPA: boolean;
  track: string;
  ariaProfile: string;
  photoAsset: UploadedAsset | null;
  bio: string;
  transcriptId: string | null;
  scoreDelta: number | null;
  videoAssetId: string | null;
  authToken: string;
}

const init = (): S => ({
  step: 'consent',
  consentData: false, consentRecord: false, consentCCPA: false,
  track: '', ariaProfile: '',
  photoAsset: null, bio: '',
  transcriptId: null, scoreDelta: null, videoAssetId: null, authToken: '',
});

function RecruitmentPage({ lane }: { lane: Lane }) {
  const navigate = useNavigate();
  const { principal, loading, getToken } = useXyqarAuth() as any;
  React.useEffect(() => { preloadConvaiScript(); }, []);
  const [s, setS] = React.useState<S>(init);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  // interviewDone = ARIA session ended OR user skipped ARIA error
  const [interviewDone, setInterviewDone] = React.useState(false);
  // ariaStarted = ARIA widget loaded and call can begin (unlock submit earlier)
  const [ariaStarted, setAriaStarted] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const photoRef = React.useRef<HTMLInputElement>(null);

  const tracks = lane === 'advisor' ? ADVISOR_TRACKS : AMBASSADOR_TRACKS;
  const upd = (k: keyof S, v: any) => setS(x => ({ ...x, [k]: v }));

  // Auth
  React.useEffect(() => {
    if (!loading && !principal) {
      sessionStorage.setItem(AUTH_RETURN_TO_KEY, window.location.pathname);
      window.location.replace(buildGenesisReturnUrl());
    }
  }, [loading, principal]);

  // Token (refresh every 50 min to avoid expiry during long interview)
  React.useEffect(() => {
    if (!principal) return;
    const refresh = () => getToken().then((t: string) => upd('authToken', t)).catch(() => {});
    refresh();
    const iv = setInterval(refresh, 50 * 60 * 1000);
    return () => clearInterval(iv);
  }, [principal, getToken]);

  const steps: Step[] = ['consent', 'lane', 'background', 'interview'];
  const stepIdx = steps.indexOf(s.step);

  const canConsent = s.consentData && s.consentRecord && s.consentCCPA;
  const canLane    = s.track.length > 0 && s.ariaProfile.length > 0;
  // Bio is optional for leaders — they can skip straight to ARIA
  const canBg      = true;
  // Submit: enabled as soon as ARIA loaded OR interview done OR transcript saved
  const canSubmit  = (ariaStarted || interviewDone || !!s.transcriptId) && !submitting;

  const handlePhoto = async (file: File) => {
    if (!s.authToken) return;
    setUploadingPhoto(true);
    try {
      const up = await uploadOnboardingAsset({
        apiBase: API_BASE, token: s.authToken,
        wizard: lane as any, step: 'background', fieldName: 'photo', file,
      });
      upd('photoAsset', up);
    } catch (e: any) {
      // Photo upload failure is non-blocking — log but don't show error
      console.warn('photo upload failed:', e?.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleVideoRecording = async ({ blob }: { blob: Blob; durationMs: number }) => {
    if (!s.authToken) return;
    try {
      const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `interview_${lane}_${Date.now()}.${ext}`, { type: blob.type });
      const up = await uploadOnboardingAsset({
        apiBase: API_BASE, token: s.authToken,
        wizard: lane as any, step: 'interview', fieldName: 'video', file,
      });
      upd('videoAssetId', up.asset_id);
    } catch { /* best-effort */ }
  };

  const handleSubmit = async () => {
    if (!principal || submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/v1/onboarding/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          wizard: lane,
          email: principal.email,
          payload: {
            lane, persona: lane,
            consent: { data: true, recording: true, ccpa: true },
            selection: { track: s.track, aria_profile: s.ariaProfile },
            background: { bio: s.bio || null, photo_asset_id: s.photoAsset?.asset_id ?? null },
            interview: {
              transcript_id: s.transcriptId,
              score_delta: s.scoreDelta,
              video_asset_id: s.videoAssetId,
            },
          },
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      setSubmitted(true);
    } catch (e: any) {
      setErr(String(e.message ?? e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !principal) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-6 px-6 text-center">
      <CheckCircle2 className="h-14 w-14 text-emerald-400" strokeWidth={1.5} />
      <h1 className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Application received.
      </h1>
      <p className="text-white/55 max-w-md text-sm leading-relaxed">
        Your {lane} profile and ARIA interview have been received.
        The founding team will review your session and reach out within 48 hours.
      </p>
      <button
        onClick={() => navigate('/')}
        className="mt-2 rounded-full border border-white/20 px-6 py-3 text-sm text-white/60 hover:border-white/40 transition"
      >
        Return home
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="px-6 py-5 border-b border-white/8 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition"
        >
          <ArrowLeft className="h-4 w-4" />Back
        </button>
        <div className="flex items-center gap-1.5">
          {steps.map((x, i) => (
            <div key={x} className={`h-1.5 rounded-full transition-all ${i <= stepIdx ? 'w-8 bg-white' : 'w-3 bg-white/20'}`} />
          ))}
        </div>
        <p className="text-[10px] uppercase tracking-widest text-white/30 font-mono">{lane}</p>
      </header>

      <main className="max-w-xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">

          {/* ── STEP 1: CONSENT ─────────────────────────────────────────── */}
          {s.step === 'consent' && (
            <motion.div key="c" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-9 h-9 rounded-full bg-white/6 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-white/50" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-white/35 font-mono">1 / 4</p>
                  <h1 className="text-xl font-bold">Consent &amp; data</h1>
                </div>
              </div>

              <div className="space-y-3">
                {([
                  {
                    key: 'consentData' as keyof S,
                    title: 'Data collection & storage',
                    body: 'Bluepinksecurity Inc. (Delaware, USA) will store your profile, answers and interview transcript for this qualification. Deletion request: privacy@bluepinksecurity.com.',
                  },
                  {
                    key: 'consentRecord' as keyof S,
                    title: 'Live recording & AI analysis',
                    body: 'Your session will be recorded. ARIA (AI) will analyze the conversation. Only the founding team accesses the recording and transcript.',
                  },
                  {
                    key: 'consentCCPA' as keyof S,
                    title: 'CCPA — California privacy rights',
                    body: 'You have the right to know, delete, and opt out of sale of your data. We do not sell personal data. Policy: bluepinksecurity.com/privacy',
                  },
                ] as Array<{ key: keyof S; title: string; body: string }>).map(({ key, title, body }) => (
                  <label key={String(key)} className="flex gap-4 p-5 rounded-2xl border border-white/10 bg-white/[0.025] cursor-pointer hover:border-white/20 transition">
                    <input
                      type="checkbox"
                      checked={s[key] as boolean}
                      onChange={e => upd(key, e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-white flex-shrink-0"
                    />
                    <div>
                      <p className="font-semibold text-sm">{title}</p>
                      <p className="text-xs text-white/45 mt-1 leading-relaxed">{body}</p>
                    </div>
                  </label>
                ))}
                <p className="text-[10px] text-white/28 px-2 leading-relaxed">
                  This {lane} seat is unpaid during the trial. Equity is evaluated after. Submitting does not create an employment relationship.
                </p>
              </div>

              <button
                onClick={() => canConsent && upd('step', 'lane')}
                disabled={!canConsent}
                className="mt-7 w-full flex items-center justify-center gap-2 bg-white text-black rounded-full py-4 font-semibold text-sm disabled:opacity-35 disabled:cursor-not-allowed hover:bg-white/90 transition"
              >
                I agree <ChevronRight className="h-4 w-4" />
              </button>
            </motion.div>
          )}

          {/* ── STEP 2: LANE ────────────────────────────────────────────── */}
          {s.step === 'lane' && (
            <motion.div key="l" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-9 h-9 rounded-full bg-white/6 flex items-center justify-center">
                  <Radar className="h-4 w-4 text-white/50" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-white/35 font-mono">2 / 4</p>
                  <h1 className="text-xl font-bold">Your lane &amp; ARIA profile</h1>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-white/35 font-mono mb-3">{lane} track</p>
                  <select
                    value={s.track}
                    onChange={e => upd('track', e.target.value)}
                    className="w-full rounded-2xl bg-black/40 border border-white/12 px-4 py-4 text-sm text-white focus:outline-none focus:border-white/30"
                  >
                    <option value="" disabled>Choose a track</option>
                    {tracks.map(t => <option key={t} value={t} className="bg-black">{t}</option>)}
                  </select>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-widest text-white/35 font-mono mb-2">ARIA interview profile</p>
                  <p className="text-xs text-white/38 mb-4">ARIA tailors its questions to your background. Pick the cluster that fits you best.</p>
                  <div className="grid gap-2.5">
                    {ARIA_PROFILES.map(p => (
                      <label
                        key={p.value}
                        className={`flex gap-4 p-4 rounded-2xl border cursor-pointer transition ${s.ariaProfile === p.value ? 'border-white/35 bg-white/7' : 'border-white/10 bg-white/[0.02] hover:border-white/18'}`}
                      >
                        <input
                          type="radio" name="ap" value={p.value}
                          checked={s.ariaProfile === p.value}
                          onChange={() => upd('ariaProfile', p.value)}
                          className="mt-0.5 h-4 w-4 accent-white flex-shrink-0"
                        />
                        <div>
                          <p className="font-semibold text-sm">{p.label}</p>
                          <p className="text-xs text-white/45 mt-0.5">{p.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-7">
                <button onClick={() => upd('step', 'consent')} className="px-5 py-3 rounded-full border border-white/12 text-sm text-white/55 hover:border-white/25 transition">Back</button>
                <button
                  onClick={() => canLane && upd('step', 'background')}
                  disabled={!canLane}
                  className="flex-1 flex items-center justify-center gap-2 bg-white text-black rounded-full py-3 font-semibold text-sm disabled:opacity-35 disabled:cursor-not-allowed hover:bg-white/90 transition"
                >
                  Continue <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 3: BACKGROUND (optional) ───────────────────────────── */}
          {s.step === 'background' && (
            <motion.div key="b" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-full bg-white/6 flex items-center justify-center">
                  <User className="h-4 w-4 text-white/50" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-white/35 font-mono">3 / 4</p>
                  <h1 className="text-xl font-bold">Profile <span className="text-white/35 font-normal text-base">(optional)</span></h1>
                </div>
              </div>

              <p className="text-sm text-white/45 mb-6 leading-relaxed">
                Optionally add a photo and short bio for our announcement post when you join. You can skip this step.
              </p>

              <div className="space-y-4">
                {/* Photo */}
                <div>
                  <input ref={photoRef} type="file" accept="image/*" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handlePhoto(f); }} />
                  <button
                    type="button"
                    onClick={() => photoRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="flex items-center gap-2.5 px-5 py-3 rounded-2xl border border-white/15 text-sm text-white/65 hover:border-white/28 transition disabled:opacity-50"
                  >
                    <ImageIcon className="h-4 w-4" strokeWidth={1.8} />
                    {uploadingPhoto ? 'Uploading…' : s.photoAsset ? '✓ Photo uploaded — change' : 'Upload profile photo (optional)'}
                  </button>
                </div>

                {/* Bio */}
                <div>
                  <textarea
                    value={s.bio}
                    onChange={e => upd('bio', e.target.value)}
                    rows={3}
                    maxLength={600}
                    placeholder="2–3 sentence bio for our social announcement (optional)"
                    className="w-full rounded-2xl bg-black/40 border border-white/12 px-4 py-4 text-sm text-white placeholder:text-white/22 focus:outline-none focus:border-white/28 resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => upd('step', 'lane')} className="px-5 py-3 rounded-full border border-white/12 text-sm text-white/55 hover:border-white/25 transition">Back</button>
                <button
                  onClick={() => upd('step', 'interview')}
                  className="flex-1 flex items-center justify-center gap-2 bg-white text-black rounded-full py-3 font-semibold text-sm hover:bg-white/90 transition"
                >
                  Start ARIA interview <Mic className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 4: ARIA INTERVIEW ───────────────────────────────────── */}
          {s.step === 'interview' && (
            <motion.div key="i" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-full bg-white/6 flex items-center justify-center">
                  <Mic className="h-4 w-4 text-white/50" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-white/35 font-mono">4 / 4 · Live interview</p>
                  <h1 className="text-xl font-bold">ARIA is ready</h1>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4 mb-5">
                <p className="text-sm text-white/60 leading-relaxed">
                  Click <strong className="text-white">"Start a call"</strong> below.
                  ARIA will ask you a few questions tailored to your{' '}
                  <strong className="text-white">{ARIA_PROFILES.find(p => p.value === s.ariaProfile)?.label ?? s.ariaProfile}</strong> profile.
                  Answer naturally, then click <strong className="text-white">Submit application</strong> when done.
                </p>
              </div>

              {/* ARIA widget */}
              <div className="rounded-2xl overflow-hidden border border-white/10 mb-5">
                {s.authToken ? (
                  <VoiceAgent
                    apiBase={API_BASE}
                    authToken={s.authToken}
                    persona={lane as any}
                    metadata={{ aria_profile: s.ariaProfile, track: s.track }}
                    allowTextFallback
                    height={460}
                    className="rounded-2xl"
                    onReady={() => setAriaStarted(true)}
                    onTranscriptSaved={({ id, score_delta }: { id: string; score_delta: number }) => {
                      upd('transcriptId', id);
                      upd('scoreDelta', score_delta);
                      setInterviewDone(true);
                    }}
                    onEnd={() => setInterviewDone(true)}
                    onError={() => {
                      // Error shown inside VoiceAgent with Retry/Skip buttons
                      // onEnd is called by Skip button inside VoiceAgent
                    }}
                  />
                ) : (
                  <div className="h-40 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-white/18 border-t-white rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* Status indicator */}
              {s.transcriptId ? (
                <div className="flex items-center gap-3 mb-5 p-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/6">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" strokeWidth={1.8} />
                  <p className="text-sm text-emerald-300">Transcript saved. Submit when ready.</p>
                </div>
              ) : interviewDone ? (
                <div className="flex items-center gap-3 mb-5 p-4 rounded-2xl border border-white/12 bg-white/[0.02]">
                  <CheckCircle2 className="h-5 w-5 text-white/50 flex-shrink-0" strokeWidth={1.8} />
                  <p className="text-sm text-white/55">Interview completed. Submit your application.</p>
                </div>
              ) : ariaStarted ? (
                <p className="text-sm text-white/38 mb-5">Complete the conversation, then submit or submit now if you prefer.</p>
              ) : (
                <p className="text-sm text-white/28 mb-5">Start the call above, then submit when done.</p>
              )}

              {err && (
                <div className="mb-4 p-4 rounded-xl border border-rose-500/25 bg-rose-500/7">
                  <p className="text-sm text-rose-400 mb-2">{err}</p>
                  <button onClick={() => setErr(null)} className="text-xs text-rose-300/60 hover:text-rose-300">Dismiss</button>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => upd('step', 'background')}
                  disabled={submitting}
                  className="px-5 py-3 rounded-full border border-white/12 text-sm text-white/55 hover:border-white/25 transition disabled:opacity-35"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="flex-1 flex items-center justify-center gap-2 bg-white text-black rounded-full py-3 font-semibold text-sm disabled:opacity-35 disabled:cursor-not-allowed hover:bg-white/90 transition"
                >
                  {submitting ? 'Submitting…' : 'Submit application'}
                  {!submitting && <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </main>
    </div>
  );
}

export function AdvisorPrequalificationPage() {
  return <RecruitmentPage lane="advisor" />;
}

export function AmbassadorPrequalificationPage() {
  return <RecruitmentPage lane="ambassador" />;
}
